import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";

import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";
import BusinessIcon from "@material-ui/icons/Business";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";

const useStyles = makeStyles(() => ({
  cell: { fontSize: 13 },
  colorDot: {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "1px solid rgba(0,0,0,0.2)",
    marginRight: 6,
    verticalAlign: "middle"
  }
}));

const emptyForm = {
  name: "",
  description: "",
  color: "",
  isActive: true,
  userIds: []
};

const Departments = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [dlg, setDlg] = useState({ open: false, editing: null, form: emptyForm });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/departments", { params: { includeUsers: true } });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Erro ao listar departamentos");
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      const list = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
      setUsers(list);
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    load();
    loadUsers();
  }, [load, loadUsers]);

  const openCreate = () =>
    setDlg({ open: true, editing: null, form: { ...emptyForm } });

  const openEdit = (item) =>
    setDlg({
      open: true,
      editing: item.id,
      form: {
        name: item.name || "",
        description: item.description || "",
        color: item.color || "",
        isActive: item.isActive !== false,
        userIds: Array.isArray(item.users) ? item.users.map((u) => u.id) : []
      }
    });

  const setField = (k, v) =>
    setDlg((prev) => ({ ...prev, form: { ...prev.form, [k]: v } }));

  const save = async () => {
    const { form, editing } = dlg;
    if (!form.name || !form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        color: form.color || null,
        isActive: !!form.isActive,
        userIds: form.userIds
      };
      if (editing) {
        await api.put(`/departments/${editing}`, payload);
      } else {
        await api.post("/departments", payload);
      }
      toast.success("Departamento salvo");
      setDlg({ open: false, editing: null, form: emptyForm });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao salvar departamento");
    }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Remover este departamento?", message: "Os usuários vinculados ficam sem departamento." }))) return;
    try {
      await api.delete(`/departments/${id}`);
      toast.success("Departamento removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao remover departamento");
    }
  };

  const columns = [
    {
      key: "name",
      label: "Nome",
      render: (item) => (
        <div className={classes.nameCell}>
          {item.color && (
            <span className={classes.colorDot} style={{ background: item.color }} />
          )}
          <Typography variant="subtitle2">{item.name}</Typography>
        </div>
      ),
    },
    {
      key: "description",
      label: "Descrição",
      render: (item) => item.description || <span className={classes.muted}>—</span>,
    },
    {
      key: "users",
      label: "Usuários",
      render: (item) => {
        const list = item.users || [];
        if (!list.length) return <span className={classes.muted}>ninguém vinculado</span>;
        return (
          <div className={classes.chips}>
            {list.map((u) => (
              <StatusChip key={u.id} tone="neutral" label={u.name} />
            ))}
          </div>
        );
      },
    },
    {
      key: "isActive",
      label: "Situação",
      render: (item) => (
        <StatusChip
          dot
          tone={item.isActive ? "success" : "neutral"}
          label={item.isActive ? "Ativo" : "Inativo"}
        />
      ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (item) => (
        <>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openEdit(item)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton size="small" onClick={() => remove(item.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Departamentos"
        count={items.length}
        subtitle="Agrupam usuários por área. Servem de filtro em relatórios e na distribuição de atendimento."
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
            Novo departamento
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={items}
        emptyIcon={<BusinessIcon />}
        emptyTitle="Nenhum departamento cadastrado"
        emptyDescription="Crie departamentos para agrupar a equipe por área e filtrar relatórios por time."
        emptyAction={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
            Novo departamento
          </Button>
        }
      />

      <Dialog
        open={dlg.open}
        onClose={() => setDlg((p) => ({ ...p, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dlg.editing ? "Editar departamento" : "Novo departamento"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nome"
            fullWidth
            margin="dense"
            variant="outlined"
            value={dlg.form.name}
            onChange={(e) => setField("name", e.target.value)}
          />
          <TextField
            label="Descrição"
            fullWidth
            margin="dense"
            variant="outlined"
            value={dlg.form.description}
            onChange={(e) => setField("description", e.target.value)}
          />
          <TextField
            label="Cor (hex, ex.: #1976d2)"
            fullWidth
            margin="dense"
            variant="outlined"
            value={dlg.form.color}
            onChange={(e) => setField("color", e.target.value)}
          />
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Usuários</InputLabel>
            <Select
              multiple
              label="Usuários"
              value={dlg.form.userIds}
              onChange={(e) => setField("userIds", e.target.value)}
              renderValue={(selected) =>
                selected
                  .map((id) => users.find((u) => u.id === id)?.name)
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  <Checkbox checked={dlg.form.userIds.indexOf(u.id) > -1} />
                  <ListItemText primary={u.name} secondary={u.email} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            style={{ marginTop: 12 }}
            control={
              <Switch
                checked={!!dlg.form.isActive}
                onChange={(e) => setField("isActive", e.target.checked)}
                color="primary"
              />
            }
            label="Ativo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg((p) => ({ ...p, open: false }))}>
            Cancelar
          </Button>
          <Button onClick={save} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Departments;
